import React, { useMemo } from 'react';
import { View, StyleSheet, Pressable, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { BORDER_RADIUS } from '@/constants/config';
import { formatRecipeCategoryLabel, getColumnMeta } from '@/utils/deckRecipeCategory';
import DeckRecipeTemplateFavoriteHeart from '@/components/Deck/DeckRecipeTemplateFavoriteHeart';

const isWeb = Platform.OS === 'web';

/**
 * @param {object} props
 * @param {object} props.template — deck template (category should be populated object when possible)
 * @param {string} [props.categoryFallbackSlug] — section slug when API returns unpopulated category ref
 * @param {{ isXS?: boolean, isSM?: boolean, isMD?: boolean }} [props.layout] — optional; if omitted, reads from `useResponsiveness` inside the card
 */
export default function DeckRecipeCard({ template, onPress, onUse, busy, featured, categoryFallbackSlug, layout }) {
  const { colors } = useTheme();
  const responsive = useResponsiveness();
  const { isXS, isSM, isMD } = layout || responsive;
  const cols = template.columns || [];
  const a = getColumnMeta(cols[0]).color;
  const b = getColumnMeta(cols[cols.length - 1] || cols[0]).color;

  const nativeCardSizing = useMemo(() => {
    if (Platform.OS === 'web') return null;
    if (isXS) return { width: '100%', alignSelf: 'stretch', maxWidth: '100%', minWidth: 0 };
    if (isSM) return { flexBasis: 252, flexGrow: 1, flexShrink: 1, maxWidth: '100%', minWidth: 0 };
    if (isMD) return { flexBasis: 264, flexGrow: 1, flexShrink: 1, maxWidth: '100%', minWidth: 0 };
    return { flexBasis: 280, flexGrow: 0, flexShrink: 1, maxWidth: '100%', minWidth: 0 };
  }, [isXS, isSM, isMD]);

  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }) => [
        styles.cardBase,
        nativeCardSizing,
        isWeb && styles.cardBaseWeb,
        {
          backgroundColor: colors.solid_color_block,
          borderColor: hovered ? colors.accent_color_primary : colors.border_color_secondary,
        },
      ]}
    >
      <View style={styles.cardStripe}>
        {isWeb ? (
          <View style={[StyleSheet.absoluteFill, { backgroundImage: `linear-gradient(90deg, ${a}, ${b})` }]} />
        ) : (
          <LinearGradient colors={[a, b]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={StyleSheet.absoluteFill} />
        )}
      </View>
      <View style={[styles.cardBody, isWeb && styles.cardBodyWeb]}>
        <View style={styles.cardCategoryRow}>
          <View style={styles.cardCategoryLeft}>
            <View style={[styles.cardDot, { backgroundColor: a }]} />
            <Text weight="bold" style={[styles.cardCategory, { color: colors.text_color_tertiary }]}>
              {formatRecipeCategoryLabel(template, categoryFallbackSlug)}
            </Text>
          </View>
          <View style={styles.cardCategoryActions}>
            {featured && (
              <View style={[styles.featuredPill, { backgroundColor: `${a}1a`, borderColor: `${a}66` }]}>
                <Icon name="star" size={8} color={a} solid />
                <Text size="xxs" weight="bold" style={{ color: a, marginLeft: 4 }}>Featured</Text>
              </View>
            )}
            <DeckRecipeTemplateFavoriteHeart template={template} />
          </View>
        </View>

        <Text weight="bold" color="primary" style={styles.cardTitle}>{template.name}</Text>
        <Text size="xxs" color="secondary" numberOfLines={2} style={styles.cardDesc}>
          {template.description}
        </Text>

        <View style={isWeb ? styles.cardFlexSpacer : null} />

        <View style={styles.iconTrack}>
          {cols.map((col, j) => {
            const m = getColumnMeta(col);
            return (
              <View key={j} style={[styles.iconTile, { backgroundColor: `${m.color}1a`, borderColor: `${m.color}55` }]}>
                <Icon name={m.icon} size={9} color={m.color} solid />
              </View>
            );
          })}
        </View>

        <Pressable
          onPress={(e) => { e?.stopPropagation?.(); onUse?.(); }}
          disabled={!!busy}
          style={({ hovered }) => [
            styles.cardCta,
            {
              backgroundColor: hovered ? `${a}EE` : `${a}22`,
              borderColor: `${a}66`,
              opacity: busy ? 0.65 : 1,
            },
          ]}
        >
          {({ hovered }) => (
            <>
              <Text size="xxs" weight="bold" style={{ color: hovered ? '#fff' : a }}>
                {busy ? 'Creating deck…' : 'Use this recipe'}
              </Text>
              <Icon name={busy ? 'spinner' : 'arrow-right'} size={9} color={hovered ? '#fff' : a} solid />
            </>
          )}
        </Pressable>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  cardBase: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.medium,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? {}
      : {
          minHeight: 260,
        }),
    ...(isWeb ? { transition: 'border-color 0.15s ease' } : {}),
  },
  cardBaseWeb: {
    width: '100%',
    minWidth: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardStripe: { height: 4, position: 'relative', overflow: 'hidden' },
  cardBody: { padding: 16 },
  cardBodyWeb: {
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  cardFlexSpacer: {
    flexGrow: 1,
    flexShrink: 0,
    minHeight: 8,
  },

  cardCategoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 8,
  },
  cardCategoryLeft: { flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 },
  cardDot: { width: 4, height: 4, borderRadius: 2 },
  cardCategory: { fontSize: 9, letterSpacing: 1.4, flexShrink: 1 },

  cardCategoryActions: { flexDirection: 'row', alignItems: 'center', gap: 4, flexShrink: 0 },

  featuredPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
  },

  cardTitle: { fontSize: 16, lineHeight: 20, letterSpacing: -0.2 },
  cardDesc: { marginTop: 4, lineHeight: 16, minHeight: 32 },

  iconTrack: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 12 },
  iconTile: {
    width: 18, height: 18, borderRadius: 5, borderWidth: 1,
    alignItems: 'center', justifyContent: 'center',
  },

  cardCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    marginTop: 10,
    ...(isWeb ? { cursor: 'pointer', transition: 'background-color 0.15s ease' } : {}),
  },
});
